import graphql from "babel-plugin-relay/macro";
import { partition } from "lodash";
import { useFragment } from "react-relay";

import {
  packageMissingPaymentMethod,
  packageOrderNeedsPayment,
  packagePaymentMethodExpired,
} from "@whoosh/features/src/Packages";

import { useGetPackageNeedingAttention_customer$key } from "./__generated__/useGetPackageNeedingAttention_customer.graphql";

export const useGetPackageNeedingAttention = (
  customerRef?: useGetPackageNeedingAttention_customer$key | null
) => {
  const customer = useFragment(
    useGetPackageNeedingAttentionFragmentString,
    customerRef
  );

  const activePackages =
    customer?.activePackages?.edges?.flatMap((edge) =>
      edge?.node ? [edge.node] : []
    ) || [];

  const upcomingPackages =
    customer?.upcomingPackages?.edges?.flatMap((edge) =>
      edge?.node ? [edge.node] : []
    ) || [];

  const [delinquentPackages, nonDelinquentPackages] = partition(
    activePackages,
    (memberPackage) => {
      const orders =
        memberPackage?.orders?.edges?.flatMap((edge) =>
          edge?.node ? [edge.node] : []
        ) || [];
      const needsPayment =
        orders.filter((order) => {
          if (!order) return false;
          return packageOrderNeedsPayment(order);
        }) || [];

      return needsPayment.length > 1;
    }
  );

  if (delinquentPackages.length > 0)
    return {
      type: "DELINQUENT" as const,
      packageType: delinquentPackages[0].package.__typename,
      memberPackageId: delinquentPackages[0].id,
    };

  const packagesNeedingPayment = nonDelinquentPackages.flatMap(
    (memberPackage) => {
      const orders =
        memberPackage?.orders?.edges?.flatMap((edge) =>
          edge?.node ? [edge.node] : []
        ) || [];

      return orders.some((order) => {
        if (!order) return false;
        return packageOrderNeedsPayment(order);
      })
        ? [memberPackage]
        : [];
    }
  );

  if (packagesNeedingPayment.length > 0)
    return {
      type: "NEEDS_PAYMENT" as const,
      packageType: packagesNeedingPayment[0].package.__typename,
      memberPackageId: packagesNeedingPayment[0].id,
    };

  const packagesMissingPaymentMethod = [
    ...activePackages,
    ...upcomingPackages,
  ].flatMap((memberPackage) =>
    packageMissingPaymentMethod(memberPackage) ? [memberPackage] : []
  );

  if (packagesMissingPaymentMethod.length > 0)
    return {
      type: "MISSING_PAYMENT_METHOD" as const,
      packageType: packagesMissingPaymentMethod[0].package.__typename,
      memberPackageId: packagesMissingPaymentMethod[0].id,
    };

  const packagesWithExpiredPaymentMethod = [
    ...activePackages,
    ...upcomingPackages,
  ].flatMap((memberPackage) =>
    packagePaymentMethodExpired(memberPackage) ? [memberPackage] : []
  );

  if (packagesWithExpiredPaymentMethod.length > 0)
    return {
      type: "EXPIRED_PAYMENT_METHOD" as const,
      packageType: packagesWithExpiredPaymentMethod[0].package.__typename,
      memberPackageId: packagesWithExpiredPaymentMethod[0].id,
    };

  return null;
};

const useGetPackageNeedingAttentionFragmentString = graphql`
  fragment useGetPackageNeedingAttention_customer on Customer {
    activePackages: memberPackages(status: ACTIVE) {
      edges {
        node {
          id
          package {
            __typename
          }
          orders {
            edges {
              node {
                ...packageOrderNeedsPayment_order
              }
            }
          }
          ...packageMissingPaymentMethod_memberPackage
          ...packagePaymentMethodExpired_memberPackage
        }
      }
    }
    upcomingPackages: memberPackages(status: UPCOMING) {
      edges {
        node {
          package {
            __typename
          }
          id
          ...packageMissingPaymentMethod_memberPackage
          ...packagePaymentMethodExpired_memberPackage
        }
      }
    }
  }
`;
